import React, { useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import get from 'lodash/get';
import classNames from 'classnames';

import ContributionsWidget from './DashboardWidgets/ContributionsWidget';
import AddPensionsWidget from './DashboardWidgets/AddPensionsWidget';
import {
  contributionWidgetClicked,
  addPensionsWidgetClicked,
  messageMeWidgetClicked,
  planActivityWidgetClicked,
  withdrawalWidgetClicked,
} from '../../../redux/modules/dashboard';
import { getAuthoritativeRetirementPlan } from '../../../redux/selectors';
import { getIsCustomerOngoingMultipleRps, getIsCustomerOptoutFullEncashment, getIsCustomerOptout } from '../../../redux/selectors/experience';
import { getIsWithdrawalAllowed, getIsAddPensionsAllowed, getIsOngoingCdsAllowed } from '../../../redux/selectors/journeys';
import {
  getPensionExpert,
  getInProgressContributionInstructions,
  getInProgressLumpSumInstructions,
} from '../../../redux/selectors/investmentAdvice';
import {
  getCustomerAgeEligibleForSerps,
  getSerpsMissingDetailsComplete,
} from '../../../redux/selectors/serps';
import withPensionExpertTier from '../../../util/pensionExpertTier/withPensionExpertTier';

import PlanActivity from '../../../assets/images/rebranding/horizontal-bars-icon.svg';
import SERPSIcon from '../../../assets/images/rebranding/SERPS_icon.svg';
import NoAdvisorAssigned from '../../../assets/images/rebranding/no-advisor-assigned.svg';
import WithdrawalIcon from '../../../assets/images/rebranding/down-arrow-inside-circle.svg';
import styles from './OngoingDashboard.css';
import CdsModal from '../../WithdrawalPage/CdsModal';

const OngoingBalance = (props) => {
  const {
    pensionAdviser,
    history,
    dispatchContributionWidgetClicked,
    dispatchAddPensionsWidgetClicked,
    dispatchMessageMeWidgetClicked,
    dispatchPlanActivityWidgetClicked,
    dispatchWithdrawalsWidgetClicked,
    isWithdrawalAllowed,
    authoritativeRp,
    inProgressContributionInstructions,
    inProgressLumpSumInstructions,
    isAddPensionsAllowed,
    isCustomerOngoingMultipleRps,
    isCustomerOptoutFullEncashment,
    isCustomerOptOut,
    isCustomerAgeEligibleForSerps,
    isSerpsMissingDetailsComplete,
    isOngoingCdsAllowed,
    isPoolExpert,
    expertRoleTitle,
  } = props;
  const [showCdsModal, setShowCdsModal] = useState(false);
  const modelDescriptor = get(authoritativeRp, 'modelDescriptor');
  const legacyInvestment = !modelDescriptor;
  const updatePensionPlan = legacyInvestment
   && !isCustomerOptoutFullEncashment
   && !isCustomerOptOut;
  const adviser = {
    imageUrl: get(pensionAdviser, 'imageUrl'),
  };

  const addMoneyLink = (contributionAmount = null) => {
    dispatchContributionWidgetClicked();
    const url = contributionAmount ? `/setup-contribution?amount=${contributionAmount}` : '/setup-contribution';
    history.push(url);
  };

  const addMoneyOptionsLink = () => {
    dispatchContributionWidgetClicked();
    history.push('/add-money-option');
  };

  const addLumpSumLink = () => {
    dispatchContributionWidgetClicked();
    history.push('/setup-lump-sum');
  };

  const addPensionsLink = () => {
    dispatchAddPensionsWidgetClicked();
    history.push('/add-pensions');
  };

  const messagePensionAdvisorLink = () => {
    dispatchMessageMeWidgetClicked();
    history.push('/inbox/new-message');
  };

  const viewPlanActivityLink = () => {
    dispatchPlanActivityWidgetClicked();
    history.push('/plan-activity');
  };

  const serpsLink = () => {
    dispatchPlanActivityWidgetClicked();
    history.push('/serps-request');
  };

  const withdrawalLink = (event) => {
    dispatchWithdrawalsWidgetClicked();
    if (isOngoingCdsAllowed) {
      setShowCdsModal(true);
      event.stopPropagation();
      return;
    }
    history.push('/withdrawals');
    event.stopPropagation();
  };

  const verifiedNetPersonalContribution = get(authoritativeRp, 'personalContributions.status') === 'verified' ? get(authoritativeRp, 'personalContributions.netValue') : 0;
  return (
    <div
      className={classNames(
        styles.ongoingWidgetsBox,
        {
          [styles.fullRowWidgets]: !isWithdrawalAllowed,
        },
      )}
    >
      {!isCustomerOptoutFullEncashment && (
        <React.Fragment>
          <ContributionsWidget
            addMoneyOnClick={addMoneyLink}
            addMoneyOptionsOnClick={addMoneyOptionsLink}
            addLumpSumOnClick={addLumpSumLink}
            inProgressContributionInstructions={inProgressContributionInstructions}
            inProgressLumpSumInstructions={inProgressLumpSumInstructions}
            isAddPensionsAllowed={isAddPensionsAllowed}
            isCustomerOngoingMultipleRps={isCustomerOngoingMultipleRps}
            authoritativeRp={authoritativeRp}
          />
          <AddPensionsWidget
            onClick={addPensionsLink}
            isCustomerOngoingMultipleRps={isCustomerOngoingMultipleRps}
            isCustomerOptoutFullEncashment={isCustomerOptoutFullEncashment}
            isAddPensionsAllowed={isAddPensionsAllowed}
          />
        </React.Fragment>
      )}
      <button
        type="button"
        className={classNames(styles.widget, styles.messagePensionAdvisorWidget, {
          [styles.fullWidthWidget]: isCustomerOptoutFullEncashment,
        })}
        onClick={messagePensionAdvisorLink}
      >
        <img className={styles.widgetImg} alt={expertRoleTitle} src={adviser.imageUrl || NoAdvisorAssigned} width="36" height="36" />
        <p className={styles.widgetText}>
          {isPoolExpert
            ? 'Message our Pension Experts'
            : `Message my ${expertRoleTitle}`}
        </p>
      </button>
      {!isCustomerOptoutFullEncashment && (
        <button
          type="button"
          className={classNames(
            styles.widget,
            {
              [styles.fullRowWidgets]: !verifiedNetPersonalContribution,
            },
          )}
          onClick={viewPlanActivityLink}
        >
          <img className={styles.widgetImg} alt="plan activity" src={PlanActivity} />
          <p className={styles.widgetText}>{'View plan activity'}</p>
        </button>
      )}
      {isCustomerAgeEligibleForSerps && !isSerpsMissingDetailsComplete && (
        <button
          type="button"
          className={classNames(
            styles.widget,
            styles.fullRowWidgets,
          )}
          onClick={serpsLink}
        >
          <img className={styles.widgetImg} alt="plan activity" src={SERPSIcon} />
          <p className={styles.widgetText}>{'Start SERPS Trace'}</p>
        </button>
      )}
      {isWithdrawalAllowed && !updatePensionPlan && (
      <button
        type="button"
        className={classNames(
          styles.widget,
          styles.fullRowWidgets,
        )}
        onClick={withdrawalLink}
      >
        <img className={styles.widgetImg} alt="plan activity" src={WithdrawalIcon} />
        <p className={styles.widgetText}>{'Withdraw from my pension'}</p>
      </button>
      )}
      {showCdsModal && (
        <CdsModal
          show={showCdsModal}
          onClose={() => setShowCdsModal(false)}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  pensionAdviser: getPensionExpert(state),
  isWithdrawalAllowed: getIsWithdrawalAllowed(state),
  authoritativeRp: getAuthoritativeRetirementPlan(state),
  inProgressContributionInstructions: getInProgressContributionInstructions(state),
  inProgressLumpSumInstructions: getInProgressLumpSumInstructions(state),
  isAddPensionsAllowed: getIsAddPensionsAllowed(state),
  isCustomerOngoingMultipleRps: getIsCustomerOngoingMultipleRps(state),
  isCustomerOptoutFullEncashment: getIsCustomerOptoutFullEncashment(state),
  isCustomerOptOut: getIsCustomerOptout(state),
  isCustomerAgeEligibleForSerps: getCustomerAgeEligibleForSerps(state),
  isSerpsMissingDetailsComplete: getSerpsMissingDetailsComplete(state),
  isOngoingCdsAllowed: getIsOngoingCdsAllowed(state),
});

const mapDispatchToProps = (dispatch) => {
  return {
    dispatchContributionWidgetClicked: () => dispatch(contributionWidgetClicked()),
    dispatchAddPensionsWidgetClicked: () => dispatch(addPensionsWidgetClicked()),
    dispatchMessageMeWidgetClicked: () => dispatch(messageMeWidgetClicked()),
    dispatchPlanActivityWidgetClicked: () => dispatch(planActivityWidgetClicked()),
    dispatchWithdrawalsWidgetClicked: () => dispatch(withdrawalWidgetClicked()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(
  withRouter(
    withPensionExpertTier(OngoingBalance),
  ),
);
